<script setup lang="ts">
import { debounce } from 'lodash';
import { onMounted, ref, useTemplateRef, computed, watch } from 'vue';
import { Grid, GridColumnProps } from '@progress/kendo-vue-grid';
import { generateColumnHeadings } from '@/lib/grid-letter-spacing';
import { useElementSize } from '@vueuse/core';
import { DashboardWidget, ApiResponse, Serie } from '@/interfaces/Dashboard';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';

const props = defineProps<{ width?: string; height?: number; widget: DashboardWidget; rawData?: ApiResponse }>();

const gridRef = useTemplateRef('gridRef');
const gridRefSize = useElementSize(gridRef);
const loading = ref(true);
const data = ref<ApiResponse | null | undefined>(props.rawData);
const dashboardStore = useDashboardStore();
const reRender = () => {};

const formattedData = computed(() => {
  if (!data.value) return null;
  const raw = data.value.data;
  const series: Serie[] = Array.isArray(raw) ? raw : raw?.series ?? [];

  if (Array.isArray(series) && raw.headings && Array.isArray(raw.headings)) {
    const items = series.map((d: any) => ({
      '': d.name,
      ...Object.fromEntries(data.value?.data?.headings.map((month: string, i: any) => [month, d.data[i]])),
    }));

    const columns = generateColumnHeadings(
      [{ '': '', ...Object.fromEntries(raw.headings.map((labels: string) => [labels, labels])) }, ...items],
      []
    );

    return {
      columns,
      items,
    };
  } else if (series) {
    const items = [Object.fromEntries(series.map((item) => [item.name, item.data]))];
    const columns = generateColumnHeadings(
      [{ ...Object.fromEntries(series.map((serie: Serie) => [serie.name])) }, ...items],
      []
    );
    return {
      columns,
      items,
    };
  }

  return null;
});

const columns = computed(() => {
  if (!formattedData.value) return;

  const totalAvailableWidth = gridRefSize.width.value;
  const originalColumns = formattedData.value.columns;
  const columnCount = originalColumns.length;
  const averageWidth = totalAvailableWidth / columnCount;

  // First pass: determine which columns are fixed and which are flexible
  let remainingWidth = totalAvailableWidth;
  let flexibleCount = 0;

  const isFixed = originalColumns.map((col: GridColumnProps) => {
    const fixed = Number(col.width) >= averageWidth;
    if (!fixed) {
      flexibleCount++;
    } else {
      remainingWidth -= Number(col.width);
    }
    return fixed;
  });

  const flexibleWidth = flexibleCount > 0 ? remainingWidth / flexibleCount : averageWidth;

  return originalColumns.map((col: GridColumnProps, i: number) => {
    if (
      gridRefSize.width.value > originalColumns.reduce((sum: number, c: GridColumnProps) => sum + Number(c.width), 0)
    ) {
      return {
        ...col,
        width: isFixed[i] ? col.width : flexibleWidth,
      };
    } else {
      return { ...col };
    }
  });
});

const gridStyle = computed(() => {
  let styles = <{ width?: string; height?: string }>{};

  if (props.height) {
    styles.height = `${props.height}px`;
  }
  if (props.width) {
    styles.width = props.width;
  } else {
    styles.width = '100%';
  }

  return styles;
});

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const fetchData = async () => {
  loading.value = true;
  const response = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
    widgetGuid: props.widget.dashboardTabWidgetGuid,
  });
  if (response) {
    data.value = response;
  } else {
    data.value = null;
  }
  loading.value = false;
};

onMounted(async () => {
  if (!data.value) {
    await fetchData();
  }
  loading.value = false;
});

watch(
  () => {
    const p = props.widget?.defaultJsonProperties?.parameters;
    return {
      results: p?.results ?? null,
      unit: p?.unit ?? null,
      benchmarkValues: p?.benchmarks?.values,
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
      groupByValue: p?.groupBy?.value ?? null,
      orderByValue: p?.orderBy?.value ?? null,
      previousMonthsValue: p?.previousMonths?.value,
      previousRangeSelected: p?.previousRange?.selected ?? false,
      previousRangeFrom: p?.previousRange?.value[0] ?? '',
      previousRangeTo: p?.previousRange?.value[1] ?? '',
    };
  },
  async (newVal, oldVal) => {
    if (!oldVal) return;
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      await debouncedFetchData();
    }
  },
  { deep: false }
);

defineExpose({ reRender });
</script>
<template>
  <grid
    v-if="!loading && formattedData"
    :pageable="false"
    scrollable="scrollable"
    ref="gridRef"
    :style="gridStyle"
    :data-items="formattedData.items"
    :row-class="() => 'tw-group'"
    :sortable="{
      allowUnsort: true,
      mode: 'multiple',
    }"
    :selectable="false"
    :columns="columns"
  >
  </grid>
  <connx-dashboard-widget-no-data v-if="!loading && formattedData === null" />
</template>
